import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { useCallback, useEffect, useState } from 'react';
import { useNotify, useTranslate } from 'react-admin';
import {
  BbWinnersTickerRow,
  bbDeleteWinnersTicker,
  bbGetWinnersTicker,
  bbUpsertWinnersTicker,
} from '../../../services/blindBoxService';
import { PermissionButton } from '../../../../../routes/components/permission-button';

export function SectionWinnersTicker({ refreshKey }: { refreshKey: number }) {
  const t = useTranslate();
  const notify = useNotify();
  const [rows, setRows] = useState<BbWinnersTickerRow[]>([]);
  const [dlg, setDlg] = useState<Partial<BbWinnersTickerRow> | null>(null);

  const load = useCallback(() => {
    bbGetWinnersTicker()
      .then(setRows)
      .catch(() => setRows([]));
  }, []);

  useEffect(() => {
    load();
  }, [load, refreshKey]);

  const save = async () => {
    if (!dlg?.walletAddress) return;
    try {
      await bbUpsertWinnersTicker({
        id: dlg.id,
        walletAddress: dlg.walletAddress,
        rewardDesc: dlg.rewardDesc!,
        timeDesc: dlg.timeDesc!,
        levelId: dlg.levelId,
        isActive: dlg.isActive!,
        displayOrder: dlg.displayOrder!,
      });
      notify(t('module.blindBox.saveOk'), { type: 'success' });
      setDlg(null);
      load();
    } catch (e: any) {
      notify(e?.message || 'Error', { type: 'error' });
    }
  };

  const del = async (id: number) => {
    if (!window.confirm(t('module.blindBox.confirmDelete'))) return;
    try {
      await bbDeleteWinnersTicker(id);
      notify(t('module.blindBox.deleteOk'), { type: 'success' });
      load();
    } catch (e: any) {
      notify(e?.message || 'Error', { type: 'error' });
    }
  };

  return (
    <Box>
      <PermissionButton resource="bb-winners-ticker" action="update">
        <Button
          sx={{ mb: 2 }}
          startIcon={<AddIcon />}
          onClick={() =>
            setDlg({
              walletAddress: '',
              rewardDesc: '',
              timeDesc: '',
              isActive: true,
              displayOrder: rows.length,
            })
          }
        >
          {t('module.blindBox.addTicker')}
        </Button>
      </PermissionButton>
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>address</TableCell>
              <TableCell>reward</TableCell>
              <TableCell>time</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((r) => (
              <TableRow key={r.id}>
                <TableCell sx={{ maxWidth: 160 }}>{r.walletAddress}</TableCell>
                <TableCell>{r.rewardDesc}</TableCell>
                <TableCell>{r.timeDesc}</TableCell>
                <TableCell align="right">
                  <PermissionButton resource="bb-winners-ticker" action="update">
                    <IconButton size="small" onClick={() => setDlg(r)}>
                      <EditOutlinedIcon fontSize="small" />
                    </IconButton>
                  </PermissionButton>
                  <PermissionButton resource="bb-winners-ticker" action="delete">
                    <IconButton size="small" onClick={() => del(r.id)}>
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </PermissionButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={!!dlg} onClose={() => setDlg(null)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('module.blindBox.addTicker')}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="walletAddress"
              value={dlg?.walletAddress || ''}
              onChange={(e) => setDlg({ ...dlg, walletAddress: e.target.value })}
              fullWidth
            />
            <TextField
              label="rewardDesc"
              value={dlg?.rewardDesc || ''}
              onChange={(e) => setDlg({ ...dlg, rewardDesc: e.target.value })}
              fullWidth
            />
            <TextField
              label="timeDesc"
              value={dlg?.timeDesc || ''}
              onChange={(e) => setDlg({ ...dlg, timeDesc: e.target.value })}
              fullWidth
            />
            <TextField
              type="number"
              label="displayOrder"
              value={dlg?.displayOrder ?? 0}
              onChange={(e) => setDlg({ ...dlg, displayOrder: parseInt(e.target.value, 10) || 0 })}
              fullWidth
            />
            <FormControlLabel
              control={
                <Switch
                  checked={!!dlg?.isActive}
                  onChange={(e) => setDlg({ ...dlg, isActive: e.target.checked })}
                />
              }
              label={t('module.blindBox.colActive')}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDlg(null)}>{t('actions.cancel')}</Button>
          <PermissionButton resource="bb-winners-ticker" action="update">
            <Button variant="contained" onClick={save}>
              {t('actions.save')}
            </Button>
          </PermissionButton>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
